import React, { useEffect } from 'react';
import { ThemeProvider } from './context/ThemeContext';
import data from './data/data.json';

const Header = React.lazy(() => import('./components/Header'));
const Hero = React.lazy(() => import('./components/Hero'));
const Skills = React.lazy(() => import('./components/Skills'));
const Experience = React.lazy(() => import('./components/Experience'));
const Projects = React.lazy(() => import('./components/Projects'));
const Education = React.lazy(() => import('./components/Education'));
const Contact = React.lazy(() => import('./components/Contact'));
const Footer = React.lazy(() => import('./components/Footer'));

function App() {
  // Add structured data for SEO
  useEffect(() => {
    // Find social URLs from the socialLinks array
    const findSocialUrl = (name: string) => {
      const socialLink = data.contact.socialLinks.find(
        (link) => link.name === name,
      );
      return socialLink ? socialLink.url : '';
    };

    // Create JSON-LD structured data
    const personSchema = {
      '@context': 'https://schema.org',
      '@type': 'Person',
      name: data.personal.name,
      url: 'https://itschinth.dev',
      jobTitle: data.personal.title,
      description: data.personal.about.join(' '),
      email: data.contact.socialLinks
        .find((link) => link.name === 'Email')
        ?.url.replace('mailto:', ''),
      sameAs: [
        findSocialUrl('GitHub'),
        findSocialUrl('LinkedIn'),
        findSocialUrl('Twitter'),
      ].filter(Boolean),
      skills: data.skills.flatMap((category) => category.skills).join(', '),
      worksFor: data.experience.map((job) => ({
        '@type': 'Organization',
        name: job.company,
        description: job.description,
      })),
      alumniOf: data.education.map((edu) => ({
        '@type': 'EducationalOrganization',
        name: edu.institution,
        description: edu.degree,
      })),
      knowsAbout: data.skills.flatMap((category) => category.skills),
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': 'https://itschinth.dev',
      },
      hasOccupation: {
        '@type': 'Occupation',
        name: data.personal.title,
        description: `Professional ${data.personal.title} with expertise in ${data.skills
          .flatMap((category) => category.skills)
          .slice(0, 5)
          .join(', ')} and more.`,
        skills: data.skills.flatMap((category) => category.skills).join(', '),
        occupationLocation: {
          '@type': 'City',
          name: data.experience[0]?.location || '',
        },
      },
    };

    // Add portfolio project data
    const portfolioSchema = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: data.projects.map((project, index) => ({
        '@type': 'SoftwareSourceCode',
        position: index + 1,
        name: project.title,
        description: project.description,
        codeRepository: project.githubLink || '',
        programmingLanguage: project.technologies,
        url: project.liveLink || '',
      })),
    };

    // Add breadcrumb structured data
    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: 'https://itschinth.dev/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'About',
          item: 'https://itschinth.dev/#about',
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: 'Skills',
          item: 'https://itschinth.dev/#skills',
        },
        {
          '@type': 'ListItem',
          position: 4,
          name: 'Experience',
          item: 'https://itschinth.dev/#experience',
        },
        {
          '@type': 'ListItem',
          position: 5,
          name: 'Projects',
          item: 'https://itschinth.dev/#projects',
        },
        {
          '@type': 'ListItem',
          position: 6,
          name: 'Education',
          item: 'https://itschinth.dev/#education',
        },
        {
          '@type': 'ListItem',
          position: 7,
          name: 'Contact',
          item: 'https://itschinth.dev/#contact',
        },
      ],
    };

    // Add the structured data to the page
    const personScript = document.createElement('script');
    personScript.type = 'application/ld+json';
    personScript.text = JSON.stringify(personSchema);
    document.head.appendChild(personScript);

    const portfolioScript = document.createElement('script');
    portfolioScript.type = 'application/ld+json';
    portfolioScript.text = JSON.stringify(portfolioSchema);
    document.head.appendChild(portfolioScript);

    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.text = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(breadcrumbScript);

    return () => {
      // Clean up when component unmounts
      document.head.removeChild(personScript);
      document.head.removeChild(portfolioScript);
      document.head.removeChild(breadcrumbScript);
    };
  }, []);

  return (
    <ThemeProvider>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
        <Header />
        <Hero />
        <Skills />
        <Experience />
        <Projects />
        <Education />
        <Contact />
        <Footer />
      </div>
    </ThemeProvider>
  );
}

export default App;
